// =============================================================================
// TASK-9267 — Permit Readiness quick fill: the PURE half.
// -----------------------------------------------------------------------------
// Permit Pete already reads every vehicle's length / width / height /
// registered weight (functions/src/index.js askPermitPete fleetContext). On
// TITS 2026-10-08 that was 0 of 14 active trucks and 0 of 13 trailers
// permit-ready, because the only way to fill a field was to open each unit's
// full edit form and find a collapsed section. The Permit Readiness screen
// now takes the four numbers inline, per unit. This file decides what a typed
// cell MEANS; roster.js owns the screen and the one write.
//
// RULES (do not "simplify" these away):
//  1. Nothing is ever filled in for the user. An empty cell stays empty — no
//     default, no make/model guess, no copy from another unit.
//  2. Over-declare, never under-declare. Feet-and-inches are converted UP to
//     the next 0.01 ft, and lowering a value that is already saved needs an
//     explicit confirm in the caller (`lowered`).
//  3. A saved value is never cleared from here (blank over a saved value is an
//     error). Removing a fact goes through the full edit form, on purpose.
//  4. Out-of-range is REFUSED, not warned. The dimension bands are the same
//     `_PERMIT_DIM_BOUNDS` reValidateVehicle uses server-side (a unit test pins
//     them equal) — a value Pete would refuse to trust must not be savable here.
//  5. Stale-write guard: if the stored value changed after the screen was
//     drawn, that field is refused rather than overwritten (`staleFields`).
//
// Loaded as a side-effect import by roster.js only (Pitfall #1 — one importer).
// =============================================================================
(function (root) {
  'use strict';

  const FIELDS = [
    { key: 'length', label: 'Length', unit: 'ft', min: 4, max: 100, integer: false, feet: true },
    { key: 'width', label: 'Width', unit: 'ft', min: 6, max: 20, integer: false, feet: true },
    { key: 'height', label: 'Height', unit: 'ft', min: 4, max: 16, integer: false, feet: true },
    // Registered (licensed) weight. The low bound catches "105.5" typed for
    // 105,500; the high bound catches an extra zero. Whole pounds only.
    { key: 'registeredWeight', label: 'Registered weight', unit: 'lbs', min: 1000, max: 400000, integer: true, feet: false },
  ];
  const BY_KEY = {};
  FIELDS.forEach((f) => { BY_KEY[f.key] = f; });

  function _isBlank(v) {
    if (v === undefined || v === null) return true;
    if (typeof v === 'string') return v.trim() === '';
    if (typeof v === 'number') return !Number.isFinite(v);
    return false;
  }

  // The stored value as a number, or null when nothing usable is stored.
  function savedNumber(v) {
    if (_isBlank(v)) return null;
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  // Round UP to 0.01 — a converted 8'7" (8.5833) is declared 8.59, never 8.58.
  function _ceil2(n) {
    return Math.ceil(n * 100 - 1e-9) / 100;
  }

  function _fmt(n, f) {
    if (n === null || n === undefined) return '';
    return f && f.integer ? Number(n).toLocaleString('en-US') : String(n);
  }

  /**
   * Read one typed cell.
   * @returns {{state:'empty'}|{state:'ok', value:number}|{state:'bad', message:string}}
   */
  function parseCell(key, raw) {
    const f = BY_KEY[key];
    if (!f) return { state: 'bad', message: 'Unknown field' };
    const s = String(raw == null ? '' : raw).trim();
    if (!s) return { state: 'empty' };

    let n = null;
    if (f.feet) {
      // 8.5  |  8'6"  |  8' 6  |  8 ft 6 in  |  13'
      const ftIn = s.match(/^(\d+(?:\.\d+)?)\s*(?:'|ft\.?|feet)\s*(?:(\d+(?:\.\d+)?)\s*(?:"|''|in\.?|inches)?)?$/i);
      if (ftIn) {
        const inches = ftIn[2] !== undefined ? Number(ftIn[2]) : 0;
        if (inches >= 12) return { state: 'bad', message: `${f.label}: inches must be under 12` };
        n = Number(ftIn[1]) + inches / 12;
        n = _ceil2(n);
      } else if (/^\d+(?:\.\d+)?$/.test(s)) {
        n = Number(s);
      } else {
        return { state: 'bad', message: `${f.label}: type feet, like 8.5 or 8'6"` };
      }
    } else {
      const clean = s.replace(/,/g, '').replace(/\s*(lbs?|pounds)\.?$/i, '');
      if (!/^\d+(?:\.\d+)?$/.test(clean)) return { state: 'bad', message: `${f.label}: numbers only, like 105500` };
      n = Number(clean);
      if (f.integer && !Number.isInteger(n)) return { state: 'bad', message: `${f.label}: whole pounds, like 105500` };
    }

    if (!Number.isFinite(n) || n <= 0) return { state: 'bad', message: `${f.label} must be more than 0` };
    if (n < f.min || n > f.max) {
      return {
        state: 'bad',
        message: `${f.label} ${_fmt(n, f)} ${f.unit} looks wrong — expected ${_fmt(f.min, f)} to ${_fmt(f.max, f)} ${f.unit}${f.feet ? ' (feet, not inches)' : ''}`,
      };
    }
    return { state: 'ok', value: n };
  }

  /**
   * Turn a card's typed cells into a write.
   * @param {object} baseline  stored values when the screen was drawn
   * @param {object} inputs    { length: '…', width: '…', … } raw strings
   * @returns {{patch:object, changes:object, errors:object, lowered:Array}}
   *   errors keyed by field; when errors is non-empty the caller must not write.
   */
  function buildPatch(baseline, inputs) {
    const base = baseline || {};
    const patch = {};
    const changes = {};
    const errors = {};
    const lowered = [];
    FIELDS.forEach((f) => {
      if (!inputs || !Object.prototype.hasOwnProperty.call(inputs, f.key)) return;
      const before = savedNumber(base[f.key]);
      const cell = parseCell(f.key, inputs[f.key]);
      if (cell.state === 'bad') { errors[f.key] = cell.message; return; }
      if (cell.state === 'empty') {
        if (before !== null) errors[f.key] = `${f.label} is saved as ${_fmt(before, f)} ${f.unit}. To remove it, use Full form.`;
        return;
      }
      if (before !== null && Math.abs(before - cell.value) < 1e-9) return;
      patch[f.key] = cell.value;
      changes[f.key] = { before, after: cell.value };
      if (before !== null && cell.value < before) {
        lowered.push({ key: f.key, label: f.label, unit: f.unit, from: before, to: cell.value, text: `${f.label}: ${_fmt(before, f)} → ${_fmt(cell.value, f)} ${f.unit}` });
      }
    });
    return { patch, changes, errors, lowered };
  }

  // Fields in `keys` whose stored value is no longer what the screen showed.
  function staleFields(baseline, fresh, keys) {
    const b = baseline || {};
    const cur = fresh || {};
    return (keys || []).filter((k) => {
      const x = savedNumber(b[k]);
      const y = savedNumber(cur[k]);
      if (x === null && y === null) return false;
      if (x === null || y === null) return true;
      return Math.abs(x - y) > 1e-9;
    });
  }

  function baselineOf(doc) {
    const out = {};
    FIELDS.forEach((f) => { out[f.key] = savedNumber(doc ? doc[f.key] : null); });
    return out;
  }

  root.PermitQuickFill = { FIELDS, parseCell, buildPatch, staleFields, baselineOf, savedNumber };
})(typeof window !== 'undefined' ? window : globalThis);
